import { put, select, take } from 'redux-saga/effects';
import notification from 'antd/lib/notification';
// internal
import history from '@app/components/router/history';
import * as ActionType from '@app/store/typings/actions/brand.d';
import * as ProjectActionType from '@app/store/typings/actions/project.d';
import * as brandService from '@app/services/brand';
import * as userService from '@app/services/user';
import * as storage from '@app/utils/localStorage';
import { handleError, convertErrorToResponse } from '@app/utils/service';
import { RootState } from '@app/store/typings/state/root';
import { UserType } from '@app/store/typings/actions/user';
import { useBrandStore } from '@app/store/brand';

export function* fetchData({ payload }: ActionType.FetchData) {
  try {
    const { data } = yield brandService.fetchBranch(payload.name);
    if (data.success) {
      yield put<ActionType.BrandType>({
        type: 'KEY_BRAND_SET_DATA',
        payload: {
          meta: data.meta,
          data: data.data,
        },
      });
      if (data.data.length === 1) {
        yield put<ActionType.BrandType>({
          type: 'KEY_BRAND_SELECT_ID',
          payload: {
            id: data.data[0]._id,
            name: data.data[0].name,
          },
        });
        const selectPage =
          data.data[0].name === 'Winnonie' ? `/winnonie/service` : `/project/selected`;
        history.push(selectPage);
      }
    }
  } catch (error) {
    handleError(error);
  }
}

export function* fetchBrandWithId({ payload }: ActionType.FetchBrandWithId) {
  try {
    const { data } = yield brandService.fetchBrandWithId(payload);
    if (data.success) {
      useBrandStore.setState({
        selected: { id: data.data._id, name: data.data.name, picture: data.data.picture },
      });
      if (data.data.name === 'Winnonie') {
        storage.setIsWinnonie('true');
      } else {
        storage.clearIsWinnonie();
      }
      yield put<ActionType.BrandType>({
        type: 'KEY_BRAND_SET_WITHID',
        payload: data.data,
      });
    }
  } catch (error) {
    handleError(error);
  }
}

export function* selectId({ payload }: ActionType.SelectID) {
  storage.setBrand(payload.id);
  storage.setBrandName(payload.name!);
  yield put<ProjectActionType.ProjectType>({
    type: 'KEY_PROJECT_CLEAR_SELECT',
  });
}

export function* manageUserInBrand({ payload }: ActionType.ManageUserInBrand) {
  try {
    const brandId: string = yield select((state: RootState) => state.brand.currentBrand._id);
    const { data } = yield brandService.manageUsersInBrand(brandId, payload.userId, payload.roleId);
    if (data.success) {
      if (payload.file) {
        yield put<UserType>({
          type: 'KEY_USER_UPLOAD_PICTURE',
          payload: {
            userId: payload.userId,
            file: payload.file,
          },
        });
      }
      yield put<ActionType.BrandType>({ type: 'KEY_BRAND_FETCH_WITHID', payload: brandId });
      notification.success({ message: 'Success', description: 'Manage admin successfully' });
      const subpath = history.location.pathname.split('/');
      const pathname = subpath.slice(0, subpath.length - 2).join('/');
      history.push(pathname);
    }
  } catch (error: any) {
    const message = error.response.data.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}

export function* deleteUserInBrand({ payload }: ActionType.DeleteUserInBrand) {
  try {
    const state: {
      brandId: string;
      userOffset: number;
      userLimit: number;
    } = yield select((state: RootState) => ({
      brandId: state.brand.currentBrand._id,
      userOffset: state.user.brandMeta.offset,
      userLimit: state.user.brandMeta.limit,
    }));
    let successDeleteNumbers: number = 0;
    for (let i = 0; i < payload.length; i++) {
      const { data } = yield brandService.deleteUsersInBrand(state.brandId, payload[i]);
      if (data.success) {
        successDeleteNumbers += 1;
      }
    }
    yield put<ActionType.BrandType>({
      type: 'KEY_BRAND_FETCH_WITHID',
      payload: state.brandId,
    });
    yield take('KEY_BRAND_SET_WITHID');
    yield put<UserType>({
      type: 'KEY_USER_FETCH_INBRAND',
      payload: { offset: state.userOffset, limit: state.userLimit },
    });
    if (successDeleteNumbers === payload.length) {
      notification.success({ message: 'Success', description: 'Delete admin successfully' });
    } else {
      notification.warning({ message: 'Warning', description: 'Incomplete delete admin' });
    }
  } catch (error: any) {
    const message = error.response.data.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}

export function* fetchBrandCreateBy() {
  try {
    const userId: string = yield select((state: RootState) => state.brand.currentBrand.createdBy);
    const { data } = yield userService.fetchUserWithId(userId);
    if (data.success) {
      yield put<ActionType.BrandType>({ type: 'KEY_BRAND_CREATEBY_SETDATA', payload: data.data });
    }
  } catch (error) {
    notification.error({ message: 'Error', description: 'Not found owner information' });
    handleError(error);
  }
}

export function* uploadBrandPicture({ payload }: ActionType.BrandUploadPicture) {
  try {
    const { data } = yield brandService.uploadBrandPicture(payload.brandId, payload.file);
    if (data.success) {
      if (payload.type === 'create') {
        yield put<ActionType.BrandType>({
          type: 'KEY_BRAND_FETCH',
          payload: {
            name: '',
          },
        });
      }
      if (payload.type === 'update') {
        yield put<ActionType.BrandType>({
          type: 'KEY_BRAND_FETCH_WITHID',
          payload: payload.brandId,
        });
      }
    }
  } catch (error) {
    const message = convertErrorToResponse(error)?.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}
